import { showDeleteConfirmation, showDeleteWithDependents } from '@modules/common';
import { Observable } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CalendarEventId, CalendarEventRequest, CalendarEventResponse, CalendarEventScheduleRequest } from '@business/entities/calendar_event';
import { CalendarTemplateEventAPI } from '@business/api/calendar_template_events';
import { CalendarTemplateId } from '@business/entities/calendar_template';
import { AddEditCalendarTemplateEventFormData } from '@modules/settings/SettingsPage/Components/SchedulesTab/Tabs/ScheduleListTab/AddEditCalendartemplateEventV2Modal/CalendarTemplateEventForm';
import { CalendarTemplateEventOrderUpdateItem, CalendarTemplateEventResponse } from '@business/entities/calendar_template_event';
import { R } from '@core/utils/r';

export class CalendarTemplateEventBloc extends BaseBloc {
  private readonly calendarTemplateEventsRepo = new Repository<CalendarTemplateEventResponse>({
    // getItemId: event => `${event.group}-${event.id}`,
    getItemId: event => event.id,
  });

  readonly calendarTemplateEvents$ = this.calendarTemplateEventsRepo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));
  readonly selectCalendarTemplateEvent = this.calendarTemplateEventsRepo.selectItem;

  onReset() {
    this.calendarTemplateEventsRepo.reset();
  }

  fetchEvents = (params?: CalendarEventScheduleRequest) => {
    return CalendarTemplateEventAPI.fetchEvents(params).pipe(
      this.calendarTemplateEventsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_calendars_template_event_error') }),
    );
  };

  fetchCalendarTemplateEvents = (id: CalendarTemplateId, request?: CalendarEventRequest) => {
    return CalendarTemplateEventAPI.fetchCalendarTemplateEvents(id, request).pipe(
      this.calendarTemplateEventsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_calendars_template_event_error') }),
    );
  };

  createCalendarTemplateEvent = (id: CalendarTemplateId, event: Partial<AddEditCalendarTemplateEventFormData>) => {
    return CalendarTemplateEventAPI.createCalendarTemplateEvent(id, event).pipe(
      this.calendarTemplateEventsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_calendar_template_event'),
      }),
    );
  };

  updateCalendarTemplateEvent = (id: string, event: Partial<AddEditCalendarTemplateEventFormData>) => {
    return CalendarTemplateEventAPI.updateCalendarTemplateEvent(id, event).pipe(
      this.calendarTemplateEventsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_calendar_template_event'),
      }),
    );
  };

  // Calendar editor drag/resize: offset, duration and/or times only — never touches other settings
  quickUpdateCalendarTemplateEvent = (id: string, changes: { offset?: number; duration?: number; startTime?: string; endTime?: string }) => {
    return CalendarTemplateEventAPI.quickUpdateCalendarTemplateEvent(id, changes).pipe(
      this.calendarTemplateEventsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_calendar_template_event'),
      }),
    );
  };

  updateEventOrder = (items: CalendarTemplateEventOrderUpdateItem[]) => {
    return CalendarTemplateEventAPI.updateCalendarTemplateEventOrder(items).pipe(
      tap(() => {
        // Sync the repo's menuOrder too — the server has the new order, but any later repo
        // emission (edit upsert, add) would otherwise re-sort the list by the stale values.
        this.calendarTemplateEventsRepo.updateMany(items.map(({ id, menuOrder }) => ({ id, changes: { menuOrder } })));
      }),
    );
  };

  /** Every event (transitively) triggered by `eventId`, from the loaded template events. */
  private descendantsOf = (eventId: CalendarEventId): CalendarTemplateEventResponse[] => {
    const all = this.calendarTemplateEventsRepo.getAllItems() as CalendarTemplateEventResponse[];
    const out: CalendarTemplateEventResponse[] = [];
    const seen = new Set<string>([String(eventId)]);
    const stack = [String(eventId)];
    while (stack.length) {
      const id = stack.pop()!;
      all.filter(e => String(e.parent?.id) === id && !seen.has(String(e.id))).forEach(e => {
        seen.add(String(e.id));
        out.push(e);
        stack.push(String(e.id));
      });
    }
    return out;
  };

  // Deleting a trigger event asks what to do with its dependents: delete only this one (they move
  // to its own trigger — offsets unchanged, so their dates shift) or delete the whole chain.
  deleteCalendarTemplateEvent = (eventId: CalendarEventId) => {
    const event = (this.calendarTemplateEventsRepo.getAllItems() as CalendarTemplateEventResponse[]).find(e => String(e.id) === String(eventId));
    const dependents = this.descendantsOf(eventId);
    const title = event?.title ?? 'this event';
    const newTrigger = event?.parent?.title ? `'${event.parent.title}'` : 'the schedule start date';

    const choice$: Observable<'one' | 'all'> = dependents.length
      ? showDeleteWithDependents(
          'Delete Template Event',
          `<strong>${title}</strong> is the trigger for <strong>${dependents.length}</strong> event${dependents.length === 1 ? '' : 's'}.<br/><br/>` +
            `<em>Delete only this</em>: those events will be triggered by ${newTrigger} instead, keeping their offsets. Check their dates afterwards.<br/>` +
            `<em>Delete all</em>: removes ${title} and all ${dependents.length} dependent events.`,
          { onlyLabel: 'Delete only this', allLabel: `Delete all ${dependents.length + 1}` },
        )
      : showDeleteConfirmation('Delete Template Event', 'Do you really want to remove this template event?').pipe(map(() => 'one' as const));

    return choice$.pipe(
      switchMap(choice =>
        CalendarTemplateEventAPI.deleteCalendarTemplateEvent(eventId, choice === 'all').pipe(
          tap(res => {
            if (choice === 'one' && event) {
              // Mirror the server's re-anchoring so the list/calendar stay in sync without a refetch
              dependents
                .filter(d => String(d.parent?.id) === String(eventId))
                .forEach(d => this.calendarTemplateEventsRepo.updateOne({ id: d.id, changes: { parent: event.parent } }));
            }
            this.calendarTemplateEventsRepo.removeMany(res.deletedIds);
          }),
        ),
      ),
      handleMessage({
        type: requestMessage('delete_calendar_template_event'),
      }),
    );
  };
}
